import React, { useState } from 'react';
import { ShoppingCart, Info } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

const formatPrice = (n) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ModularProductCard = ({ product, onAdd }) => {
  const [selected, setSelected] = useState(product.dimensions[0]);
  const [zoom, setZoom] = useState(false);

  const handleAdd = () => {
    onAdd({
      id: `${product.id}_${selected.key}`,
      name: `${product.name} — ${selected.label}`,
      code: `${product.code}/${selected.key}`,
      price: selected.price,
      oldPrice: null,
      discount: 0,
      image: product.image,
      stock: product.stock
    });
  };

  return (
    <div className="group bg-white border border-gray-100 rounded-md overflow-hidden hover:shadow-xl hover:border-gray-200 transition-all duration-300 flex flex-col">
      <button
        type="button"
        onClick={() => setZoom(true)}
        className="relative aspect-[4/3] overflow-hidden bg-gray-50 cursor-zoom-in block w-full text-left focus:outline-none focus:ring-2 focus:ring-amber-500"
        aria-label="Mărește imaginea"
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </button>
      <ImageLightbox open={zoom} src={product.image} alt={product.name} onClose={() => setZoom(false)} />

      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-2">
          {product.name}
        </h3>
        {product.description && (
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">{product.description}</p>
        )}
        <div className="text-[11px] text-gray-500 mb-3">Cod: {product.code}</div>

        <label className="text-xs font-medium text-gray-700 mb-1.5 block">Alege dimensiunea:</label>
        <select
          value={selected.key}
          onChange={(e) => setSelected(product.dimensions.find((d) => d.key === e.target.value))}
          className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm bg-white focus:outline-none focus:border-amber-500 mb-3 cursor-pointer"
        >
          {product.dimensions.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label} — {formatPrice(d.price)} lei
            </option>
          ))}
        </select>

        <div className="text-xs font-medium text-emerald-600 mb-2">{product.stock}</div>

        <div className="mt-auto">
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-xs text-gray-500">Preț</span>
            <span className="text-lg font-bold text-gray-900">{formatPrice(selected.price)} lei</span>
          </div>
          <button
            onClick={handleAdd}
            className="w-full flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-amber-500 text-white text-xs font-semibold uppercase tracking-wider py-2.5 rounded-sm transition-colors"
          >
            <ShoppingCart className="w-4 h-4" />
            Adauga in cos
          </button>
        </div>
      </div>
    </div>
  );
};

const DemoCard = ({ product }) => {
  const [zoom, setZoom] = useState(false);
  return (
    <div className="group bg-white border-2 border-amber-300 border-dashed rounded-md overflow-hidden flex flex-col">
      <button
        type="button"
        onClick={() => setZoom(true)}
        className="relative aspect-[4/3] overflow-hidden bg-gray-50 cursor-zoom-in block w-full focus:outline-none"
        aria-label="Mărește imaginea"
      >
        <span className="absolute top-3 left-3 z-10 bg-amber-500 text-white text-[11px] font-bold uppercase px-2.5 py-1 rounded-sm shadow tracking-wide">
          Doar pentru ilustrare
        </span>
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </button>
      <ImageLightbox open={zoom} src={product.image} alt={product.name} onClose={() => setZoom(false)} />
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{product.name}</h3>
        <p className="text-xs text-gray-600 leading-relaxed flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          {product.description}
        </p>
      </div>
    </div>
  );
};

const ModularSection = ({ id, products, onAdd }) => {
  const [galleryOpen, setGalleryOpen] = useState({ open: false, src: '', alt: '' });

  const gallery = [
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/A%20randari%202025/jauzea%20suprem/Panou%20de%20gard%20Jaluzea%20Supreme%201530x2000mm-1800x1161.webp', alt: 'Panou Jaluzea Supreme — vedere instalată' },
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/Poze-lucrari/Jaluzea/Untitled-412-1800x1161.webp', alt: 'Lucrare reală — gard Jaluzea în curte' },
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/Poze-lucrari/Jaluzea/Untitled-41-1800x1161.webp', alt: 'Detaliu instalat — Jaluzea Supreme' },
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/supreme-ranforsata-1800x1161.webp', alt: 'Vedere ranforsare — Jaluzea Supreme' },
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/Sectiuni/Sectiuni-profile/cadru-jaluzea-1800x1161.webp', alt: 'Secțiune profil cadru Jaluzea' },
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/Sectiuni/Sectiuni-profile/23-010-1800x1161.webp', alt: 'Secțiune profil lamela tip Z' },
    { src: 'https://www.virtuoso.ro/image/cachewebp/catalog/Sectiuni/Sectiuni-porti/Sectiune-poarta-16-1800x1161.webp', alt: 'Secțiune transversală poartă/gard' },
    { src: 'https://www.virtuoso.ro/image/catalog/Componenta-Panou/Gard-Jaluzea.jpg', alt: 'Compoziție gard Jaluzea — schemă' }
  ];

  return (
    <section id={id} className="py-14 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <div className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold mb-2">Piese modulare</div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Construiește-ți propriul panou de gard
          </h2>
          <p className="text-gray-600">
            Combină lamelele tip Z cu un cadru pentru a obține panoul de gard în dimensiunea dorită.
            Alege dimensiunea pentru fiecare piesă din meniul de mai jos.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {products.map((p) =>
            p.type === 'demo' ? (
              <DemoCard key={p.id} product={p} />
            ) : (
              <ModularProductCard key={p.id} product={p} onAdd={onAdd} />
            )
          )}
        </div>

        {/* Galerie informativă */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <div className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold mb-2">Galerie informativă</div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Cum arată panoul Jaluzea instalat
            </h3>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto">
              Imagini din lucrări reale, secțiuni tehnice și detalii de profil. Click pe orice imagine pentru a o mări.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {gallery.map((g, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setGalleryOpen({ open: true, src: g.src, alt: g.alt })}
                className="group relative aspect-square overflow-hidden rounded-sm bg-white border border-gray-200 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label={g.alt}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[11px] text-white leading-tight line-clamp-2">{g.alt}</p>
                </div>
              </button>
            ))}
          </div>
          <ImageLightbox
            open={galleryOpen.open}
            src={galleryOpen.src}
            alt={galleryOpen.alt}
            onClose={() => setGalleryOpen({ open: false, src: '', alt: '' })}
          />
        </div>
      </div>
    </section>
  );
};

export default ModularSection;
